import React, { useEffect, useState, useRef, lazy } from 'react';
import { FadeIn } from './ui/FadeIn';
import { CheckIcon, XIcon, MessageCircleIcon, ChevronLeftIcon, ChevronRightIcon, ImageIcon } from 'lucide-react';
export function AudienceSection() {
  const isFor = ['Eres una mujer empleada que sueña con un plan B seguro — sin renunciar a tu empleo actual.', 'Ya emprendes de forma artesanal y quieres que tus productos se perciban como premium.', 'Amas el bienestar y buscas un negocio con alma, propósito y estética.', 'La tecnología te intimida, pero sabes seguir un paso a paso claro cuando alguien te guía.'];
  const isNotFor = ['Buscas "dinero fácil" sin dedicarle cariño a tu producto.', 'No estás dispuesta a invertir al menos 45 minutos al día en tu futuro.', 'Tienes una gran empresa y buscas producción masiva industrializada.'];
  const testimonialSlides = [{
    id: 1,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI1.webp?v=1772397985'
  }, {
    id: 2,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI2.webp?v=1772397985'
  }, {
    id: 3,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI3.webp?v=1772397985'
  }, {
    id: 4,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI4.webp?v=1772397985'
  }, {
    id: 5,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI5.webp?v=1772397985'
  }, {
    id: 6,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI6.webp?v=1772397985'
  }, {
    id: 7,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI7.webp?v=1772397985'
  }, {
    id: 8,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI8.webp?v=1772397985'
  }, {
    id: 9,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI9.webp?v=1772397985'
  }, {
    id: 10,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI10.webp?v=1772397985'
  }, {
    id: 11,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI11.webp?v=1772397985'
  }, {
    id: 12,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI12.webp?v=1772397985'
  }, {
    id: 13,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI13.webp?v=1772397985'
  }, {
    id: 14,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI14.webp?v=1772397985'
  }, {
    id: 15,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI15.webp?v=1772397985'
  }, {
    id: 16,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI16.webp?v=1772397985'
  }, {
    id: 17,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI17.webp?v=1772397985'
  }, {
    id: 18,
    image: 'https://cdn.shopify.com/s/files/1/0981/8108/7598/files/TESTI18.webp?v=1772397985'
  }];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    // Calculate active slide index
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length === 0) return;
    const scrollCenter = el.scrollLeft + el.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    children.forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const distance = Math.abs(scrollCenter - childCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });
    setActiveIndex(closestIndex);
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
    };
  }, []);
  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('div')?.offsetWidth ?? 240;
    el.scrollBy({
      left: direction === 'left' ? -(cardWidth + 16) : cardWidth + 16,
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    if (children[index]) {
      children[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };
  return <section className="py-14 px-6 lg:px-8 bg-tea-cream" data-id="element-1">
      <div className="max-w-6xl mx-auto" data-id="element-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20" data-id="element-3">
          {/* Who this is for */}
          <FadeIn direction="right" data-id="element-4">
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border-t-4 border-tea-green h-full" data-id="element-5">
              <h3 className="text-2xl font-heading font-bold text-tea-green mb-8" data-id="element-6">
                Este plan es para ti si…
              </h3>
              <ul className="space-y-6" data-id="element-7">
                {isFor.map((item, index) => <li key={index} className="flex items-start" data-id="element-8">
                    <div className="bg-tea-sage/50 rounded-full p-1 mr-4 flex-shrink-0 mt-1" data-id="element-9">
                      <CheckIcon className="w-5 h-5 text-tea-green" data-id="element-10" />
                    </div>
                    <span className="text-tea-charcoal/80 text-lg leading-relaxed" data-id="element-11">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          {/* Who this is NOT for */}
          <FadeIn delay={0.2} direction="left" data-id="element-12">
            <div className="bg-white/50 rounded-2xl p-8 md:p-10 border border-tea-charcoal/10 h-full" data-id="element-13">
              <h3 className="text-2xl font-heading font-bold text-tea-charcoal/60 mb-8" data-id="element-14">
                Este plan NO es para ti si…
              </h3>
              <ul className="space-y-6" data-id="element-15">
                {isNotFor.map((item, index) => <li key={index} className="flex items-start opacity-70" data-id="element-16">
                    <div className="bg-gray-200 rounded-full p-1 mr-4 flex-shrink-0 mt-1" data-id="element-17">
                      <XIcon className="w-5 h-5 text-gray-500" data-id="element-18" />
                    </div>
                    <span className="text-tea-charcoal/70 text-lg leading-relaxed" data-id="element-19">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>

        {/* Testimonials Carousel */}
        <FadeIn delay={0.4} data-id="element-20">
          <div className="max-w-5xl mx-auto" data-id="element-21">
            <div className="flex items-center justify-between mb-6" data-id="element-22">
              <div className="inline-flex items-center space-x-2 text-tea-gold font-bold uppercase tracking-widest text-sm" data-id="element-23">
                <MessageCircleIcon className="w-5 h-5" data-id="element-24" />
                <span data-id="element-25">Lo que dicen las nuevas dueñas de marcas de té</span>
              </div>

              {/* Navigation arrows */}
              <div className="flex items-center gap-2" data-id="element-26">
                <button onClick={() => scroll('left')} disabled={!canScrollLeft} className="w-10 h-10 rounded-full border border-tea-gold/40 flex items-center justify-center text-tea-gold transition-all duration-200 hover:bg-tea-gold/20 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Anterior" data-id="element-27">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-28" />
                </button>
                <button onClick={() => scroll('right')} disabled={!canScrollRight} className="w-10 h-10 rounded-full border border-tea-gold/40 flex items-center justify-center text-tea-gold transition-all duration-200 hover:bg-tea-gold/20 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Siguiente" data-id="element-29">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-30" />
                </button>
              </div>
            </div>

            {/* Carousel container */}
            <div className="relative" data-id="element-31">
              {/* Fade edges */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-tea-cream to-transparent z-10 pointer-events-none" style={{
              opacity: canScrollLeft ? 1 : 0,
              transition: 'opacity 0.3s'
            }} data-id="element-32" />
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-tea-cream to-transparent z-10 pointer-events-none" style={{
              opacity: canScrollRight ? 1 : 0,
              transition: 'opacity 0.3s'
            }} data-id="element-33" />

              <div ref={scrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2" style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }} data-id="element-34">
                {testimonialSlides.map(slide => <div key={slide.id} className="relative flex-shrink-0 w-[65vw] sm:w-[45vw] md:w-[220px] snap-start" data-id="element-35">
                    <div className="aspect-[9/16] rounded-2xl bg-white border border-tea-sage/30 flex flex-col items-center justify-center gap-3 shadow-sm overflow-hidden" data-id="element-36">
                      <img src={slide.image} alt={`Testimonio ${slide.id}`} className="w-full h-full object-cover rounded-2xl" loading="lazy" data-id="element-37" />
                    </div>
                  </div>)}
              </div>

              {/* Scroll indicator dots (mobile) */}
              <div className="flex justify-center items-center gap-1.5 mt-4 md:hidden" data-id="element-38">
                {testimonialSlides.map((slide, i) => {
                const distance = Math.abs(i - activeIndex);
                if (distance > 4) return null;
                return <div key={slide.id} className={`rounded-full transition-all duration-300 ${activeIndex === i ? 'w-6 h-1.5 bg-tea-gold' : distance <= 2 ? 'w-1.5 h-1.5 bg-tea-gold/30' : 'w-1 h-1 bg-tea-gold/15'}`} data-id="element-39" />;
              })}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}